import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getFoodRules, createFoodRule, deleteFoodRule, getFoodLogs, createFoodLog, extractErrorMessage } from '../services/api';
import { FaPlus, FaTrash, FaUtensils, FaBook } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

function FoodRules({ user }) {
  const toast = useToast();
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('rules');
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [savingLog, setSavingLog] = useState(false);
  const [formErrorRule, setFormErrorRule] = useState(null);
  const [formErrorLog, setFormErrorLog] = useState(null);
  const [newRule, setNewRule] = useState({
    rule_text: '',
    category: '',
    restriction_type: ''
  });
  const [newLog, setNewLog] = useState({
    user_id: '',
    meal_type: 'breakfast',
    description: '',
    calories: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rulesData, logsData] = await Promise.all([
        getFoodRules(),
        getFoodLogs()
      ]);
      setRules(rulesData.rules || rulesData || []);
      setLogs(logsData.logs || logsData || []);
    } catch (error) {
      console.error('Error fetching food data:', error);
      toast.error(extractErrorMessage(error, 'Ernährungsdaten konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = async (e) => {
    e.preventDefault();
    if (savingRule) return;
    setSavingRule(true);
    setFormErrorRule(null);
    try {
      await createFoodRule(newRule);
      setNewRule({ rule_text: '', category: '', restriction_type: '' });
      setShowRuleModal(false);
      toast.success('Ernährungsregel erstellt');
      fetchData();
    } catch (error) {
      console.error('Error creating food rule:', error);
      setFormErrorRule(extractErrorMessage(error, 'Fehler beim Erstellen der Regel'));
    } finally {
      setSavingRule(false);
    }
  };

  const handleCreateLog = async (e) => {
    e.preventDefault();
    if (savingLog) return;
    setSavingLog(true);
    setFormErrorLog(null);
    try {
      await createFoodLog({ ...newLog, calories: newLog.calories ? parseInt(newLog.calories) : null });
      setNewLog({ user_id: '', meal_type: 'breakfast', description: '', calories: '', notes: '' });
      setShowLogModal(false);
      toast.success('Ernährungseintrag erstellt');
      fetchData();
    } catch (error) {
      console.error('Error creating food log:', error);
      setFormErrorLog(extractErrorMessage(error, 'Fehler beim Erstellen des Eintrags'));
    } finally {
      setSavingLog(false);
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Regel wirklich löschen?')) return;
    try {
      await deleteFoodRule(id);
      toast.success('Ernährungsregel gelöscht');
      fetchData();
    } catch (error) {
      console.error('Error deleting food rule:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen der Regel'));
    }
  };

  const closeRuleModal = () => {
    if (savingRule) return;
    setShowRuleModal(false);
    setFormErrorRule(null);
  };

  const closeLogModal = () => {
    if (savingLog) return;
    setShowLogModal(false);
    setFormErrorLog(null);
  };

  const getMealTypeLabel = (type) => {
    const labels = { breakfast: 'Frühstück', lunch: 'Mittagessen', dinner: 'Abendessen', snack: 'Snack' };
    return labels[type] || type;
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaUtensils /> Ernährungsregeln</h1>
          <div className="header-actions">
            <button className="add-btn" onClick={() => activeTab === 'rules' ? setShowRuleModal(true) : setShowLogModal(true)}>
              <FaPlus /> {activeTab === 'rules' ? 'Neue Regel' : 'Neuer Eintrag'}
            </button>
          </div>
        </div>

        <div className="tab-bar">
          <button className={`tab-btn ${activeTab === 'rules' ? 'active' : ''}`} onClick={() => setActiveTab('rules')}>
            <FaBook /> Regeln
          </button>
          <button className={`tab-btn ${activeTab === 'logs' ? 'active' : ''}`} onClick={() => setActiveTab('logs')}>
            <FaUtensils /> Ernährungstagebuch
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : activeTab === 'rules' ? (
          <div className="data-table-container">
            {rules.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Regel</th>
                    <th>Kategorie</th>
                    <th>Einschränkungstyp</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map(rule => (
                    <tr key={rule.id}>
                      <td>{rule.rule_text}</td>
                      <td><span className="badge">{rule.category}</span></td>
                      <td><span className="badge">{rule.restriction_type}</span></td>
                      <td>
                        <button className="icon-btn danger" onClick={() => handleDeleteRule(rule.id)} title="Löschen">
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Keine Ernährungsregeln vorhanden</div>
            )}
          </div>
        ) : (
          <div className="data-table-container">
            {logs.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Mahlzeit</th>
                    <th>Beschreibung</th>
                    <th>Kalorien</th>
                    <th>Notizen</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id}>
                      <td>{new Date(log.created_at || log.timestamp).toLocaleDateString('de-DE')}</td>
                      <td><span className="badge">{getMealTypeLabel(log.meal_type)}</span></td>
                      <td>{log.description}</td>
                      <td>{log.calories || '-'}</td>
                      <td>{log.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Keine Einträge vorhanden</div>
            )}
          </div>
        )}

        {showRuleModal && (
          <div className="modal-overlay" onClick={closeRuleModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neue Ernährungsregel</h2>
              {formErrorRule && <div className="shared-form-error-banner">{formErrorRule}</div>}
              <form onSubmit={handleCreateRule}>
                <div className="form-group">
                  <label>Regel *</label>
                  <textarea
                    value={newRule.rule_text}
                    onChange={e => setNewRule({ ...newRule, rule_text: e.target.value })}
                    placeholder="Regeltext eingeben..."
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Kategorie</label>
                  <input
                    type="text"
                    value={newRule.category}
                    onChange={e => setNewRule({ ...newRule, category: e.target.value })}
                    placeholder="z.B. Getränke, Süßigkeiten..."
                  />
                </div>
                <div className="form-group">
                  <label>Einschränkungstyp</label>
                  <select
                    value={newRule.restriction_type}
                    onChange={e => setNewRule({ ...newRule, restriction_type: e.target.value })}
                  >
                    <option value="">Auswählen...</option>
                    <option value="forbidden">Verboten</option>
                    <option value="limited">Eingeschränkt</option>
                    <option value="required">Pflicht</option>
                    <option value="allowed">Erlaubt</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeRuleModal} disabled={savingRule}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingRule}>{savingRule ? 'Speichert\u2026' : 'Erstellen'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showLogModal && (
          <div className="modal-overlay" onClick={closeLogModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neuer Ernährungseintrag</h2>
              {formErrorLog && <div className="shared-form-error-banner">{formErrorLog}</div>}
              <form onSubmit={handleCreateLog}>
                <div className="form-group">
                  <UserSelect
                    value={newLog.user_id}
                    onChange={id => setNewLog({ ...newLog, user_id: id })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Mahlzeit *</label>
                  <select
                    value={newLog.meal_type}
                    onChange={e => setNewLog({ ...newLog, meal_type: e.target.value })}
                    required
                  >
                    <option value="breakfast">Frühstück</option>
                    <option value="lunch">Mittagessen</option>
                    <option value="dinner">Abendessen</option>
                    <option value="snack">Snack</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Beschreibung *</label>
                  <textarea
                    value={newLog.description}
                    onChange={e => setNewLog({ ...newLog, description: e.target.value })}
                    placeholder="Was wurde gegessen?"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Kalorien</label>
                  <input
                    type="number"
                    value={newLog.calories}
                    onChange={e => setNewLog({ ...newLog, calories: e.target.value })}
                    placeholder="Optional"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={newLog.notes}
                    onChange={e => setNewLog({ ...newLog, notes: e.target.value })}
                    placeholder="Zusätzliche Notizen..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeLogModal} disabled={savingLog}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingLog}>{savingLog ? 'Speichert\u2026' : 'Eintragen'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default FoodRules;
